/**
 * Geode Voice (UPT-2000 lot V0 §5.1) — bouton micro : états idle → connecting
 * Adapté de JARVIS Local (licence MIT, © 2026 Thomas Berton) — voir THIRD_PARTY_NOTICE.md.
 * → listening ↔ speaking → idle, plus error ; clic = démarrer / arrêter ;
 * `aria-pressed`, libellé selon l'état ; refus du micro → message clair ;
 * 503 → bascule proposée vers le simulateur en local, message « pas encore
 * configurée » ailleurs. Taille tactile ≥ 44 px.
 *
 * Visible seulement si l'add-on core-addon-voice est actif pour le locataire
 * ou si le simulateur local est activé (`isVoiceVisible`).
 */
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from '@/react-intl';
import { useActiveModules } from '@/uptk/core/hooks/useActiveModules';
import { createVoiceSession, isVoiceApiError } from './voiceApi';
import { MockVoiceSession } from './MockVoiceSession';
import { RealtimeVoiceSession } from './RealtimeVoiceSession';
import { getVoiceSession, setVoiceSession } from './voiceSessionHolder';
import { useVoiceStore } from './voiceStore';
import { isLocalProfile, isVoiceVisible, readVoiceMockFlag } from './voiceVisibility';
import type { VoiceContext, VoiceState } from './voiceTypes';

export type VoiceButtonProps = {
  context: VoiceContext;
  /** Contexte de page courant (même forme que le tiroir du copilote). */
  pageContext?: string;
};

const stateLabelKey = (state: VoiceState): string => {
  switch (state) {
    case 'connecting':
      return 'voice.button.connecting';
    case 'listening':
      return 'voice.button.listening';
    case 'speaking':
      return 'voice.button.speaking';
    case 'error':
      return 'voice.button.error';
    default:
      return 'voice.button.start';
  }
};

export function VoiceButton({ context, pageContext = '' }: VoiceButtonProps) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const { modules } = useActiveModules();
  const state = useVoiceStore((store) => store.state);
  const errorId = useVoiceStore((store) => store.errorId);
  const errorValues = useVoiceStore((store) => store.errorValues);
  const mockActive = useVoiceStore((store) => store.mockActive);
  const level = useVoiceStore((store) => store.level);
  const [proposal, setProposal] = useState(false);

  const visible = useMemo(() => isVoiceVisible(modules), [modules]);

  const start = useCallback(async () => {
    const store = useVoiceStore.getState();
    store.setError(null);
    setProposal(false);

    // Simulateur : drapeau local ou bascule proposée acceptée.
    if ((isLocalProfile() && readVoiceMockFlag()) || store.mockActive) {
      store.setMockActive(true);
      // UPT-2124 J2a : la réponse « hors fiche » de « que dois-je faire ? » est traduite ici (le simulateur n'a pas d'intl).
      const session = new MockVoiceSession({ pageContext, translate: (id) => intl.formatMessage({ id }) });
      setVoiceSession(session);
      await session.start();
      return;
    }

    let session: RealtimeVoiceSession | null = null;
    try {
      // Langue seule (« en-US » → « en ») : le serveur choisit ses consignes par langue.
      const language = typeof intl.locale === 'string' ? intl.locale.split('-')[0] : 'fr';
      const info = await createVoiceSession(language, context);
      session = new RealtimeVoiceSession({
        info,
        context,
        pageContext,
        onExpired: () => useVoiceStore.getState().setError('voice.error.expired'),
      });
      setVoiceSession(session);
      await session.start();
    } catch (error) {
      setVoiceSession(null);
      // Échec en cours de route (négociation refusée…) : micro et connexion libérés (revue CORE 11.10).
      await session?.stop().catch(() => undefined);
      if (isVoiceApiError(error)) {
        if (error.code === 'voice.not_configured') {
          // Serveur sans Azure OpenAI Realtime : le simulateur est proposé partout où le micro est visible
          // (add-on actif ou poste local), plus seulement en local — retour d'Olivier sur DEV (UPT-2098, 12.10).
          setProposal(true);
          useVoiceStore.getState().setError('voice.error.notConfiguredLocal');
        } else if (error.code === 'voice.budget_exhausted') {
          // V1a : plafond mensuel atteint. Pas de simulateur proposé : ce n'est pas un moyen de contourner un plafond.
          useVoiceStore.getState().setError('voice.error.budget');
        } else if (error.code === 'voice.rate_limited') {
          useVoiceStore.getState().setError('voice.error.rateLimited');
        } else if (error.code === 'voice.addon_inactive') {
          // En local, le micro est visible sans l'add-on : le simulateur est proposé (revue CORE 11.10).
          if (isLocalProfile()) {
            setProposal(true);
          }
          useVoiceStore.getState().setError('voice.error.addonInactive');
        } else {
          useVoiceStore.getState().setError('voice.error.upstream');
        }
        return;
      }
      // Refus du micro par le navigateur.
      const name = (error as { name?: string })?.name ?? '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        useVoiceStore.getState().setError('voice.error.micDenied');
      } else {
        useVoiceStore.getState().setError('voice.error.upstream');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `intl` change avec la locale, déjà dans les dépendances
  }, [context, intl.locale, pageContext]);

  const stop = useCallback(async () => {
    const session = getVoiceSession();
    setVoiceSession(null);
    await session?.stop();
    useVoiceStore.getState().setState('idle');
  }, []);

  const acceptMockProposal = useCallback(() => {
    useVoiceStore.getState().setMockActive(true);
    setProposal(false);
    void start();
  }, [start]);

  if (!visible) {
    return null;
  }

  const active = state === 'listening' || state === 'speaking' || state === 'connecting';
  // En-tête étroit du tiroir du copilote : message et bandeau en icône (infobulle + texte pour les
  // lecteurs d'écran) au lieu d'un texte écrasé en colonne (revue CORE 11.10).
  const compact = context === 'copilot';

  return (
    <span className="inline-flex items-center gap-2" data-voice-exclude="true">
      <button
        type="button"
        aria-pressed={active}
        aria-label={t(stateLabelKey(state))}
        title={t(stateLabelKey(state))}
        onClick={() => {
          if (active) {
            void stop();
          } else {
            void start();
          }
        }}
        className={`relative flex min-h-11 min-w-11 items-center justify-center rounded-full border !p-0 ${
          state === 'error'
            ? 'border-danger-solid bg-danger-subtle text-danger-text'
            : active
              ? 'border-success-solid bg-success-subtle text-success-text'
              : 'border-portal-border bg-portal-surface text-portal-ink'
        }`}
      >
        <i className={`fa ${active ? 'fa-stop' : 'fa-microphone'}`} aria-hidden="true" />
        {state === 'listening' ? (
          <span className="sr-only">{t('voice.button.listening')}</span>
        ) : null}
        {/* Indicateur de niveau audio sobre (barre CSS, §11 JARVIS). */}
        {active ? (
          <span
            aria-hidden="true"
            className="absolute bottom-1 left-1/2 h-0.5 -translate-x-1/2 rounded bg-success-solid transition-all"
            style={{ inlineSize: `${Math.round(8 + level * 24)}px` }}
          />
        ) : null}
      </button>
      {errorId ? (
        compact ? (
          <span role="alert" title={t(errorId, errorValues ?? undefined)} className="text-sm text-danger-text">
            <i className="fa fa-exclamation-circle" aria-hidden="true" />
            <span className="sr-only">{t(errorId, errorValues ?? undefined)}</span>
          </span>
        ) : (
          <span role="alert" className="text-xs text-danger-text">
            {t(errorId, errorValues ?? undefined)}
          </span>
        )
      ) : null}
      {proposal ? (
        <button
          type="button"
          onClick={acceptMockProposal}
          className="whitespace-nowrap rounded-md border border-info-solid bg-info-subtle px-2 py-1 text-xs text-info-text"
        >
          {t('voice.mock.useSimulator')}
        </button>
      ) : null}
      {mockActive ? (
        compact ? (
          <span
            title={t('voice.mock.banner')}
            className="rounded-full border border-warning-solid bg-warning-subtle px-1.5 py-0.5 text-xs text-warning-text"
          >
            <i className="fa fa-flask" aria-hidden="true" />
            <span className="sr-only">{t('voice.mock.banner')}</span>
          </span>
        ) : (
          <span className="whitespace-nowrap rounded-full border border-warning-solid bg-warning-subtle px-2 py-0.5 text-xs text-warning-text">
            {t('voice.mock.banner')}
          </span>
        )
      ) : null}
    </span>
  );
}

export default VoiceButton;
